import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useMemo
} from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import {
  DeleteForeverRounded as DeleteIcon,
  EditRounded as EditIcon,
  AddRounded as AddIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  GridCellEditCommitParams,
  GridColumns
} from '@mui/x-data-grid';

import { Gap, Spacer } from './spacer';
import { CenteredRow } from './layout';
import {
  EditTextDialog,
  EditTextDialogProps
} from './dialogs/edit-text-dialog';
import { ConfirmDeleteAlert } from './confirm-delete-alert';
import { StyledHeader } from './table';

import {
  Item,
  Size,
  useSpot,
  ConfirmDeleteDialogState,
  ItemRow
} from '../framework';

const StyledMeasurementHeader = styled(StyledHeader)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  font-size: 0.9rem;
`;

const EditorContainer = styled(Box)`
  position: relative;
  height: 400px;
  min-height: 400px;
  flex: 1 1 auto;
`;

export const converItemsToItemRows = (items: Item[]) =>
  items.reduce((accum, item) => {
    const result = [...accum];

    let found = result.find(
      i => i.id === `${item.brandName}-${item.productName}-${item.gender}`
    );

    if (!found) {
      found = {
        itemIds: [],
        id: `${item.brandName}-${item.productName}-${item.gender}`,
        brandName: item.brandName,
        productName: item.productName,
        gender: item.gender,
        measurements: [],
        limits: [],
        labels: {},
        ttsSkew: item.ttsSkew,
        adjustments: item.adjustments
      };
      result.push(found);
    }

    found.limits.push(item.limit);
    found.measurements.push(item.measurementName);
    found.itemIds.push(item.id);
    found.adjustments = [
      ...(item.adjustments ?? []),
      ...(found.adjustments ?? [])
    ];

    item.sizes.forEach(size => {
      if (!found) {
        return;
      }

      found.labels[size.label] = found.labels[size.label] ?? {};
      found.labels[size.label][item.measurementName] = {
        value: size.value,
        maxValue: size.maxValue,
        aliases: size.aliases,
        displayName: size.displayName,
        id: size.id,
        itemId: item.id
      };
    });

    return result;
  }, [] as ItemRow[]) ?? [];

// Helper function to format size value for editing
const formatSizeValueForEditing = (
  value: number,
  maxValue: number | null
): string => {
  if (maxValue && maxValue !== value) {
    return `${value}-${maxValue}`;
  }
  return value.toString();
};

// Helper function to parse edited size value
const parseSizeValueFromEditing = (
  editedValue: string
): { value: number; maxValue: number | null } => {
  const trimmed = editedValue.trim();
  if (trimmed.includes('-')) {
    const parts = trimmed.split('-').map(p => p.trim());
    if (parts.length === 2 && parts[0] && parts[1]) {
      const value = parseInt(parts[0], 10);
      const maxValue = parseInt(parts[1], 10);
      if (!isNaN(value) && !isNaN(maxValue) && value >= 0 && maxValue >= 0) {
        return { value, maxValue };
      }
    }
    // If there's a dash but parsing failed, it's invalid
    throw new Error('Invalid size value format');
  }
  const value = parseInt(trimmed, 10);
  if (!isNaN(value) && value >= 0) {
    return { value, maxValue: null };
  }
  throw new Error('Invalid size value format');
};

export function SizeChartItemEditor({
  item,
  refresh,
  onLoadingChanged,
  onDeleteItems,
  disabled
}: {
  item: ItemRow;
  refresh?: (itemId: string | undefined) => unknown;
  onLoadingChanged?: (loading: boolean) => unknown;
  onDeleteItems?: (ids: number[]) => unknown;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { command, loading } = useSpot();

  useEffect(() => {
    onLoadingChanged && onLoadingChanged(loading);
  }, [loading, onLoadingChanged]);

  const [confirmSizeDelete, setConfirmSizeDelete] =
    useState<ConfirmDeleteDialogState<number[]> | null>(null);

  const [confirmAliasDelete, setConfirmAliasDelete] =
    useState<ConfirmDeleteDialogState<{
      sizes: Size[];
      alias: string;
    }> | null>(null);

  const [showEditText, setShowEditText] = useState<EditTextDialogProps | null>(
    null
  );

  const deleteSizes = useCallback(
    async (ids: number[] | undefined) => {
      setConfirmSizeDelete(null);

      const oldItemId = item?.id;

      if (ids) {
        const promises = ids.map(id =>
          command(`size/${id}`, undefined, {
            method: 'DELETE'
          })
        );

        await Promise.all(promises);
        refresh && (await refresh(oldItemId));
      }
    },
    [command, setConfirmSizeDelete, refresh, item]
  );

  const confirmDeleteSize = useCallback(
    (ids: number[]) => {
      if (ids) {
        setConfirmSizeDelete({
          text: t('deleteSizeConfirmationText'),
          title: t('deleteSizeConfirmation'),
          value: ids,
          onConfirm: (sizeIds: number[] | undefined) => deleteSizes(sizeIds)
        });
      }
    },
    [setConfirmSizeDelete, deleteSizes, t]
  );

  const deleteAlias = useCallback(
    async (sizes?: Size[], alias?: string) => {
      setConfirmAliasDelete(null);

      const oldItemId = item?.id;

      if (sizes && alias) {
        const promises = sizes.map(size =>
          size.aliases
            ? command(
                `size/${size.id}`,
                {
                  ...size,
                  aliases: size.aliases.filter(a => a !== alias)
                },
                {
                  method: 'PATCH'
                }
              )
            : null
        );

        await Promise.all(promises);
        refresh && (await refresh(oldItemId));
      }
    },
    [command, item, refresh]
  );

  const confirmDeleteAlias = useCallback(
    (sizes: Size[], alias: string, event?: React.MouseEvent) => {
      if (alias) {
        event?.stopPropagation();
        setConfirmAliasDelete({
          text: t('deleteAliasConfirmationText'),
          title: t('deleteAliasConfirmation'),
          value: { sizes, alias },
          onConfirm: (payload: { sizes: Size[]; alias: string } | undefined) =>
            deleteAlias(payload?.sizes, payload?.alias)
        });
      }
    },
    [setConfirmAliasDelete, deleteAlias, t]
  );

  const addAlias = useCallback(
    async (sizes: Size[], alias) => {
      setShowEditText(null);

      const oldItemId = item?.id;

      if (sizes && alias) {
        const promises = sizes.map(size =>
          command(
            `size/${size.id}`,
            {
              ...size,
              aliases: [...(size.aliases ?? []), alias]
            },
            {
              method: 'PATCH'
            }
          )
        );

        await Promise.all(promises);
        refresh && (await refresh(oldItemId));
      }
    },
    [command, item, refresh]
  );

  const editDisplayName = useCallback(
    async (sizes: Size[], displayName) => {
      setShowEditText(null);

      const oldItemId = item?.id;

      if (sizes) {
        const promises = sizes.map(size =>
          command(
            `size/${size.id}`,
            {
              ...size,
              displayName: displayName || null
            },
            {
              method: 'PATCH'
            }
          )
        );

        await Promise.all(promises);
        refresh && (await refresh(oldItemId));
      }
    },
    [command, item, refresh]
  );

  const updateSizeValue = useCallback(
    async (
      sizeId: number,
      label: string,
      newValue: number,
      newMaxValue: number | null
    ) => {
      const oldItemId = item?.id;

      // Find the size object to update
      const allSizes = Object.values(item?.labels ?? {}).reduce(
        (acc, labelSizes) => [...acc, ...Object.values(labelSizes)],
        [] as any[]
      );
      const sizeToUpdate = allSizes.find(s => s.id === sizeId);

      if (sizeToUpdate) {
        await command(
          `size/${sizeId}`,
          {
            ...sizeToUpdate,
            label,
            value: newValue,
            maxValue: newMaxValue
          },
          {
            method: 'PATCH'
          }
        );

        refresh && (await refresh(oldItemId));
      }
    },
    [command, item, refresh]
  );

  const deleteMeasurementItem = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const measurement = event.currentTarget.id;
      const itemToDelete = Object.values(item.labels)[0]?.[measurement]?.itemId;

      onDeleteItems && onDeleteItems([itemToDelete]);
    },
    [onDeleteItems, item]
  );

  // Handle cell edit commit for size values
  const onCellEditCommit = useCallback(
    async (params: GridCellEditCommitParams) => {
      try {
        // Parse the field to get measurement name
        const measurement = params.field;
        const label = params.id as string;

        // Get the size object for this label and measurement
        const sizeData = item?.labels[label]?.[measurement];
        if (!sizeData) return;

        // Parse the new value
        const { value, maxValue } = parseSizeValueFromEditing(
          params.value as string
        );

        // Update the size value
        await updateSizeValue(sizeData.id, label, value, maxValue);
      } catch (error) {
        console.error('Error updating size value:', error);
        // Could show a toast notification here
      }
    },
    [item, updateSizeValue]
  );

  // Transform data for DataGrid
  const { rows, columns } = useMemo(() => {
    if (!item) return { rows: [], columns: [] };

    // Create columns
    const gridColumns: GridColumns = [
      {
        field: 'label',
        headerName: t('label'),
        width: 80,
        minWidth: 60,
        align: 'center',
        sortable: false,
        filterable: false
      }
    ];

    // Add measurement columns
    item.measurements.forEach((measurement, i) => {
      gridColumns.push({
        field: measurement,
        headerName: `${t(measurement)} (${item.limits[i]})`,
        width: 140,
        minWidth: 100,
        editable: !disabled && !!refresh,
        align: 'right',
        sortable: false,
        filterable: false,
        renderHeader: () => (
          <StyledMeasurementHeader>
            {t(measurement)} ({item?.limits[i]})
            <Gap size={1} />
            {!disabled && refresh && onDeleteItems && (
              <IconButton
                id={measurement}
                onClick={deleteMeasurementItem}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </StyledMeasurementHeader>
        )
      });
    });

    // Add display name column
    gridColumns.push({
      field: 'displayName',
      headerName: t('displayName'),
      width: 140,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: params => {
        const label = params.id as string;
        const sizes = item.labels[label];
        const displayNames = item.measurements.reduce(
          (displayNames: string[], measurement: string) => {
            if (
              sizes[measurement] &&
              sizes[measurement].displayName &&
              !displayNames.includes(sizes[measurement].displayName as string)
            ) {
              return [
                ...displayNames,
                sizes[measurement].displayName as string
              ];
            }
            return displayNames;
          },
          []
        );

        return (
          <CenteredRow>
            {displayNames.join(', ')}
            <Spacer />
            {!disabled && refresh && (
              <IconButton
                className="grid-icon-button"
                style={{ flex: 0 }}
                onClick={e => {
                  e.stopPropagation();
                  setShowEditText({
                    text: t('editDisplayNameText'),
                    label: t('editDisplayNameLabel'),
                    title: t('editDisplayNameTitle'),
                    initialValue: Object.values(sizes)[0].displayName,
                    dialogOpen: true,
                    onClose: () => setShowEditText(null),
                    onSubmit: displayName =>
                      editDisplayName(
                        Object.values(sizes).map(size => ({
                          ...size,
                          label
                        })),
                        displayName
                      )
                  });
                }}
                title={t('assignTag')}
                size="large"
              >
                <EditIcon color="primary" />
              </IconButton>
            )}
          </CenteredRow>
        );
      }
    });

    // Add aliases column
    gridColumns.push({
      field: 'aliases',
      headerName: t('aliases'),
      minWidth: 150,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => {
        const label = params.id as string;
        const sizes = item.labels[label];
        const aliases = item.measurements
          .reduce(
            (aliases, measurement) => [
              ...aliases,
              ...(sizes[measurement]?.aliases ?? [])
            ],
            [] as string[]
          )
          .filter((alias, index, arr) => arr.indexOf(alias) === index);

        return (
          <CenteredRow>
            {aliases.map(alias => (
              <Fragment key={alias}>
                <Chip
                  label={alias}
                  disabled={disabled || !onDeleteItems}
                  onDelete={event =>
                    confirmDeleteAlias(
                      Object.values(sizes).map(size => ({
                        ...size,
                        label
                      })),
                      alias,
                      event
                    )
                  }
                />
                <Gap />
              </Fragment>
            ))}
            <Spacer />
            {!disabled && refresh && (
              <IconButton
                className="grid-icon-button"
                style={{ flex: 0 }}
                onClick={e => {
                  // Add a small delay to ensure proper focus management
                  e.stopPropagation();
                  setShowEditText({
                    text: t('addAliasText'),
                    label: t('addAliasLabel'),
                    title: t('addAliasTitle'),
                    dialogOpen: true,
                    onClose: () => setShowEditText(null),
                    onSubmit: alias =>
                      addAlias(
                        Object.values(sizes).map(size => ({
                          ...size,
                          label
                        })),
                        alias
                      )
                  });
                }}
                title={t('addAliasTitle')}
                size="large"
              >
                <AddIcon color="primary" />
              </IconButton>
            )}
          </CenteredRow>
        );
      }
    });

    // Add actions column
    if (!disabled && refresh && onDeleteItems) {
      gridColumns.push({
        field: 'actions',
        headerName: '',
        width: 60,
        minWidth: 60,
        sortable: false,
        filterable: false,
        renderCell: params => {
          const label = params.id as string;
          const sizes = item.labels[label];
          return (
            <IconButton
              className="grid-icon-button"
              onClick={e => {
                e.stopPropagation();
                confirmDeleteSize(Object.values(sizes).map(s => s.id));
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>
          );
        }
      });
    }

    // Create rows
    const gridRows = Object.entries(item.labels).map(([label, sizes]) => {
      const row: any = { id: label, label };

      // Add measurement values
      item.measurements.forEach(measurement => {
        const sizeData = sizes[measurement];
        if (sizeData) {
          row[measurement] = formatSizeValueForEditing(
            sizeData.value,
            sizeData.maxValue
          );
        } else {
          row[measurement] = '';
        }
      });

      return row;
    });

    return { rows: gridRows, columns: gridColumns };
  }, [
    item,
    disabled,
    refresh,
    onDeleteItems,
    t,
    deleteMeasurementItem,
    confirmDeleteSize,
    editDisplayName,
    confirmDeleteAlias,
    addAlias
  ]);

  return (
    <>
      {showEditText && <EditTextDialog {...showEditText} />}
      {confirmSizeDelete?.onConfirm && (
        <ConfirmDeleteAlert
          id="confirmSizeDelete"
          title={confirmSizeDelete?.title ?? ''}
          text={confirmSizeDelete?.text ?? ''}
          open={!!confirmSizeDelete}
          onConfirm={confirmSizeDelete?.onConfirm}
          value={confirmSizeDelete?.value}
          keepMounted
        />
      )}
      {confirmAliasDelete?.onConfirm && (
        <ConfirmDeleteAlert
          id="confirmAliasDelete"
          title={confirmAliasDelete?.title ?? ''}
          text={confirmAliasDelete?.text ?? ''}
          open={!!confirmAliasDelete}
          onConfirm={confirmAliasDelete?.onConfirm}
          value={confirmAliasDelete?.value}
          keepMounted
        />
      )}
      <EditorContainer>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={r => r.id}
          onCellEditCommit={onCellEditCommit}
          loading={loading}
          pageSize={100}
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
        />
      </EditorContainer>
    </>
  );
}
